/**
 * DocumentLifecycle Component
 *
 * Diagram of one document's lifecycle — block cards on the overview, a compact
 * rail once the detail of a step is open — below `md` the rail becomes a plain select.
 */
import { useId, type ReactNode } from 'react';
import { Select } from '@insio/ui-toolkit';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { LifecycleStepCards } from './LifecycleStepCards';
import { LifecycleStepRail } from './LifecycleStepRail';
import { useInvoiceLifecycleSteps } from './useInvoiceLifecycleSteps';
import { usePurchaseOrderLifecycleSteps } from './usePurchaseOrderLifecycleSteps';
import { useRequisitionLifecycleSteps } from './useRequisitionLifecycleSteps';
import { stepLabelKey, type LifecycleDocument, type LifecycleStepKey } from './types';

export interface DocumentLifecycleProps {
  /** Document whose lifecycle is drawn. */
  document: LifecycleDocument;
  /** Step whose detail is open; null on the overview. */
  openStep: LifecycleStepKey | null;
  /** Opens the detail of a step. */
  onSelectStep: (step: LifecycleStepKey) => void;
  /** Card slots of the overview diagram; unused ones stay empty, so cards line up across documents. */
  slots?: number;
  /** Drawn before the step rail of an open step, e.g. the way back to the overview. */
  leading?: ReactNode;
}

/** Each document reads its own settings — the hooks stay small and the diagram generic. */
function useLifecycleSteps(document: LifecycleDocument) {
  const invoiceSteps = useInvoiceLifecycleSteps();
  const requisitionSteps = useRequisitionLifecycleSteps();
  const purchaseOrderSteps = usePurchaseOrderLifecycleSteps();
  switch (document) {
    case 'invoice':
      return invoiceSteps;
    case 'requisition':
      return requisitionSteps;
    case 'purchaseOrder':
      return purchaseOrderSteps;
  }
}

export function DocumentLifecycle({
  document,
  openStep,
  onSelectStep,
  slots,
  leading
}: DocumentLifecycleProps) {
  const t = useFeatureTranslation('systemSettings.automation.lifecycle');
  const labelId = useId();
  const steps = useLifecycleSteps(document);

  return (
    <div className='flex flex-col gap-sm'>
      <div className='flex items-baseline justify-between gap-md'>
        <h2
          id={labelId}
          className={
            openStep
              ? 'text-xs font-semibold uppercase tracking-wide text-foreground-muted'
              : 'text-base font-semibold text-foreground-base'
          }
        >
          {t(document)}
        </h2>
        <p className='hidden sm:block text-xs text-foreground-disabled'>{t('description')}</p>
      </div>

      {openStep ? (
        <div className='flex items-center gap-sm'>
          {leading}
          {/* The rail does not survive a phone-width screen; the overview cards do. */}
          <div className='md:hidden min-w-0 flex-1'>
            <Select
              value={openStep}
              onChange={value => onSelectStep(value as LifecycleStepKey)}
              options={steps.map(step => ({
                value: step.key,
                label: t(`steps.${stepLabelKey(document, step.key)}.title`)
              }))}
              height='lg'
              testId={`lifecycle-${document}-step-select`}
            />
          </div>

          <div className='hidden md:block min-w-0 flex-1'>
            <LifecycleStepRail
              document={document}
              steps={steps}
              openStep={openStep}
              onSelectStep={onSelectStep}
              labelId={labelId}
            />
          </div>
        </div>
      ) : (
        <LifecycleStepCards
          document={document}
          steps={steps}
          onSelectStep={onSelectStep}
          labelId={labelId}
          slots={slots}
        />
      )}
    </div>
  );
}
